import React, { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppLayout } from './layouts/AppLayout';
import { Home } from './pages/Home';
import { PublicLanding } from './pages/PublicLanding';
import { PublicForm } from './pages/PublicForm';
import { ThankYou } from './pages/ThankYou';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { Dashboard } from './pages/app/Dashboard';
import { Leads } from './pages/app/Leads';
import { LeadDetail } from './pages/app/LeadDetail';
import { Pipeline } from './pages/app/Pipeline';
import { Trash } from './pages/app/Trash';
import { Forms } from './pages/app/Forms';
import { FormEditor } from './pages/app/FormEditor';
import { Landings } from './pages/app/Landings';
import { LandingEditor } from './pages/app/LandingEditor';
import { Team } from './pages/app/Team';
import { Settings } from './pages/app/Settings';
import { Integrations } from './pages/app/Integrations';
import { Toaster } from './components/ui/Toast';

/** Protección de la zona privada con la sesión real de Supabase. */
function RequireAuth({
  children
}: {
  children: ReactNode;
}) {
  const {
    session,
    restoring
  } = useAuth();
  const location = useLocation();
  if (restoring) {
    return <div className="flex min-h-screen w-full items-center justify-center bg-background" aria-busy="true" data-id="element-0" data-mp="2zmwl5831y4h0">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1" data-mp="33orkw85c49so" />
        <span className="sr-only" data-id="element-2" data-mp="38o4h3ji3it7x">Cargando tu espacio de trabajo…</span>
      </div>;
  }
  if (!session) {
    return <Navigate to="/login" state={{
      from: location.pathname
    }} replace data-id="element-3" data-mp="nvj2rdpwcaoo" />;
  }
  return <>{children}</>;
}
export function App() {
  return <BrowserRouter data-id="element-4" data-mp="stkj3qvxtvwg">
      <AuthProvider data-id="element-5" data-mp="sssq8ntq6c9v">
        <Routes data-id="element-6" data-mp="swgmhuqk1csa">
          {/* Zona pública */}
          <Route path="/" element={<Home data-id="element-8" data-mp="34x4lozr5n72" />} data-id="element-7" data-mp="35gmj5pekbl3" />
          <Route path="/landing/:slug" element={<PublicLanding data-id="element-10" data-mp="3udknzzefn0bz" />} data-id="element-9" data-mp="38b4mqky1kbc" />
          <Route path="/formulario/:slug" element={<PublicForm data-id="element-12" data-mp="d4nwdiwxho3g" />} data-id="element-11" data-mp="3uc9jtyh2rqkc" />
          <Route path="/gracias/:slug" element={<ThankYou data-id="element-14" data-mp="d3wd97kipiu2" />} data-id="element-13" data-mp="d4xobc1ja5bs" />
          <Route path="/login" element={<Login data-id="element-16" data-mp="851n1kk80rzx" />} data-id="element-15" data-mp="d2bhxfqdpvv5" />
          <Route path="/registro" element={<Register data-id="element-18" data-mp="83i1pfjpjqra" />} data-id="element-17" data-mp="86w6eufugc1w" />
          <Route path="/recuperar-contrasena" element={<ForgotPassword data-id="element-20" data-mp="3gm65dtoft15g" />} data-id="element-19" data-mp="3gl4t5lvoq8m5" />

          {/* Zona privada */}
          <Route path="/app" element={<RequireAuth data-id="element-22" data-mp="3bnct24kvzg6v">
                <AppLayout data-id="element-23" data-mp="3bo4isyrsyjp8" />
              </RequireAuth>} data-id="element-21" data-mp="3bmtjbb5e3y3x">
            <Route index element={<Navigate to="/app/dashboard" replace data-id="element-25" data-mp="3qbzcoihjqu3w" />} data-id="element-24" data-mp="3qbfud0nzi3pc" />
            <Route path="dashboard" element={<Dashboard data-id="element-27" data-mp="3ljfzhmmru0zm" />} data-id="element-26" data-mp="3lkr41oo1jcrs" />
            <Route path="leads" element={<Leads data-id="element-29" data-mp="2xuwt5cna7ya6" />} data-id="element-28" data-mp="3lj67xum2vlrt" />
            <Route path="leads/:id" element={<LeadDetail data-id="element-31" data-mp="2xtc05rgm8nef" />} data-id="element-30" data-mp="2xu3ku78od0px" />
            <Route path="pipeline" element={<Pipeline data-id="element-33" data-mp="2swbq75gnb0i1" />} data-id="element-32" data-mp="2xsk3sef72ln3" />
            <Route path="papelera" element={<Trash data-id="element-35" data-mp="2stzdp7tnpicd" />} data-id="element-34" data-mp="2sv0mf5n79sqx" />
            <Route path="formularios" element={<Forms data-id="element-37" data-mp="18czlufkf77kw" />} data-id="element-36" data-mp="18e19rkmjbok1" />
            <Route path="formularios/nuevo" element={<Forms data-id="element-39" data-mp="1d47z5movfd3c" />} data-id="element-38" data-mp="18cgfzx81o9n8" />
            <Route path="formularios/:id" element={<FormEditor data-id="element-41" data-mp="1d6kmkfdeefns" />} data-id="element-40" data-mp="1d5jac7knbn4h" />
            <Route path="landings" element={<Landings data-id="element-43" data-mp="ygmtbl0m2zrq" />} data-id="element-42" data-mp="yfvbdczxt0jx" />
            <Route path="landings/nueva" element={<Landings data-id="element-45" data-mp="13eewxcamcabr" />} data-id="element-44" data-mp="yheowvu7kxi0" />
            <Route path="landings/:id/editor" element={<LandingEditor data-id="element-47" data-mp="13clpvvw3fo38" />} data-id="element-46" data-mp="13fq1hebw1m3x" />
            <Route path="equipo" element={<Team data-id="element-49" data-mp="1r5ai3qkkk7a1" />} data-id="element-48" data-mp="1r4iqsry08vpk" />
            <Route path="configuracion" element={<Settings data-id="element-51" data-mp="1w33puo1it9cf" />} data-id="element-50" data-mp="1r525lzscz3zd" />
            <Route path="integraciones" element={<Integrations data-id="element-53" data-mp="1w2kgvwtulvaj" />} data-id="element-52" data-mp="1w2araacptq5d" />
          </Route>

          <Route path="*" element={<Navigate to="/" replace data-id="element-55" data-mp="1i2sw9d8ttgnk" />} data-id="element-54" data-mp="1i443xo5e78jy" />
        </Routes>
        {/* Confirmaciones de copiado, publicación y guardado */}
        <Toaster position="bottom-right" data-id="element-56" data-mp="1m3cwipop50dx" />
      </AuthProvider>
    </BrowserRouter>;
}